import { ref, computed, type ComputedRef } from 'vue';
import { useLocalStorage } from '@vueuse/core';

export interface LogMessage {
    cat?: string;
    emoji?: string;
    msg: string | unknown[];
    [key: string]: unknown;
}

export interface LoggingToggles {
    [category: string]: boolean;
}

/**
 * Enhanced console logging system with category-based filtering
 *
 * Usage:
 * console.log({ cat: 'router', msg: 'Route changed to /dashboard' });
 * console.info({ cat: 'api', msg: 'API call completed', data: response });
 * console.warn({ cat: 'auth', msg: 'Token expired' });
 * console.error({ cat: 'router', msg: 'Navigation failed', error });
 *
 * Categories are case-insensitive: 'Router', 'ROUTER', and 'router' are treated the same.
 * Categories can be toggled on/off via localStorage key 'sysarb_logging_toggles'
 */
export interface ToggleConfig {
    key: string;
    defaultState: boolean;
    description: string;
    emoji: string;
}

export interface UseConsoleLoggerReturn {
    initConsoleLogger: () => void;
    restoreConsole: () => void;
    toggleCategory: (category: string, enabled: boolean) => void;
    enableCategory: (category: string) => void;
    disableCategory: (category: string) => void;
    availableCategories: ComputedRef<string[]>;
    isCategoryEnabled: (category: string) => boolean;
    resetCategoryToggles: () => void;
    loggingToggles: ComputedRef<LoggingToggles>;
    isDevMode: ComputedRef<boolean>;
    toggleConfigurations: ToggleConfig[];
    loggingMode: ComputedRef<'simple' | 'verbose'>;
    setLoggingMode: (mode: 'simple' | 'verbose') => void;
}

export function useConsoleLogger(): UseConsoleLoggerReturn {
    const usesDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

    const autoCategorizeMessages = [
        { match: 'GTM-Support', category: 'GTM' },
        { match: 'gtm.js', category: 'GTM' },
        { match: '[VueGtm]', category: 'GTM' },
        { match: 'OpenTelemetry', category: 'OpenTelemetry' },
        { match: 'AG Grid:', category: 'aggrid' },
    ];

    const defaultToggleStates: Record<string, Omit<ToggleConfig, 'key'>> = {
        logger: { defaultState: true, description: 'Advanced Console Logger', emoji: '🪵' },
        global: { defaultState: true, description: 'Global logging toggle', emoji: '🌐' },
        resgate: { defaultState: true, description: 'Resgate Logging', emoji: '⭐️' },
        router: { defaultState: true, description: 'Router navigation logging', emoji: '🧭' },
        packages: { defaultState: true, description: 'Package-related logging', emoji: '📦' },
        uselanguage: { defaultState: true, description: 'Language utility logging', emoji: '🌍' },
        analytics: { defaultState: true, description: 'Analytics tracking logging', emoji: '📊' },
        gtm: { defaultState: true, description: 'Google Tag Manager logging', emoji: '🏷️' },
        labs: { defaultState: true, description: 'Labs feature logging', emoji: '🧪' },
        opentelemetry: { defaultState: true, description: 'OpenTelemetry', emoji: '💗' },
        aggrid: { defaultState: true, description: 'AgGrid', emoji: '🧮' },
    };

    // Extract default states for localStorage
    const defaultToggleStatesObject: LoggingToggles = {};
    for (const [key, config] of Object.entries(defaultToggleStates)) {
        defaultToggleStatesObject[key] = config.defaultState;
    }

    const loggingToggles = useLocalStorage<LoggingToggles>(
        'sysarb_logging_toggles',
        defaultToggleStatesObject
    );

    const loggingMode = useLocalStorage<'simple' | 'verbose'>(
        'sysarb_logging_mode',
        'simple'
    );

    const isDevMode = ref(import.meta.env.DEV === true);

    // Store original console methods
    const originalConsole = {
        log: window.console.log,
        info: window.console.info,
        warn: window.console.warn,
        error: window.console.error,
        debug: window.console.debug,
    };

    /**
     * Auto-categorize messages based on content matching and filename matching
     */
    function autoCategorizeMessage(message: unknown, filename?: string): string | undefined {
        for (const rule of autoCategorizeMessages) {
            // Check message content (case insensitive)
            if (typeof message === 'string' && message.toLowerCase().includes(rule.match.toLowerCase())) {
                return rule.category;
            }
            // Check filename if provided (case insensitive)
            if (filename?.toLowerCase().includes(rule.match.toLowerCase())) {
                return rule.category;
            }
        }
        return undefined;
    }

    /**
     * Get emoji for a category
     */
    function getCategoryEmoji(category: string): string {
        const normalizedCategory = category.toLowerCase();
        if (normalizedCategory in defaultToggleStates) {
            return defaultToggleStates[normalizedCategory].emoji;
        }
        return '';
    }

    /**
     * Extract filename from full path
     */
    function extractFileName(filePath: string): string {
        // Remove query parameters and fragments first
        const cleaned = filePath.split('?')[0].split('#')[0];

        // Extract just the filename (last part after / or \)
        const fileName = cleaned.split('/').pop()?.split('\\').pop() ?? cleaned;

        return fileName;
    }

    /**
     * Parse location from a stack line using the primary pattern (line:column at end)
     */
    function parseLineColumnPattern(line: string): { lineNum: string; column: string; functionName: string; fileName: string } | null {
        const lineColumnMatch = line.match(/:(\d+):(\d+)\)?\s*$/);
        if (lineColumnMatch == null) return null;

        const lineNum = lineColumnMatch[1];
        const column = lineColumnMatch[2];
        const withoutLineColumn = line.replace(/:\d+:\d+\)?\s*$/, '');

        const functionMatch = withoutLineColumn.match(/at\s+(\S+)\s+\(/);
        const functionName = functionMatch != null && functionMatch.length >= 2 ? functionMatch[1] : '';

        const file = withoutLineColumn
            .replace(/^at\s+/, '')
            .replace(/^\S+\s+\(/, '')
            .replace(/\)\s*$/, '');

        const fileName = extractFileName(file);
        return { lineNum, column, functionName, fileName };
    }

    /**
     * Parse location from a stack line using the fallback pattern
     */
    function parseSimplePattern(line: string): { lineNum: string; column: string; functionName: string; fileName: string } | null {
        if (line.length > 1000) return null;

        const simpleMatch = line.match(/at\s+(?:(\S+)\s+\()?([^:)]{1,500}):(\d+):(\d+)\)?/);
        if (simpleMatch == null || simpleMatch.length < 4) return null;

        const [, functionName, file, lineNum, column] = simpleMatch;
        const fileName = extractFileName(file);
        return {
            lineNum,
            column,
            functionName: functionName && functionName !== '' ? functionName : '',
            fileName
        };
    }

    /**
     * Format location string from parsed components
     */
    function formatLocationString(lineNum: string, column: string, functionName: string, fileName: string): string {
        const functionPart = functionName !== '' ? ` - ${functionName}` : '';
        return `${fileName}:${lineNum}:${column}${functionPart}`;
    }

    /**
     * Parse a single stack line to extract location information
     */
    function parseLocationFromLine(line: string): string | null {
        if (line === '') return null;

        // Try primary pattern first
        const primaryResult = parseLineColumnPattern(line);
        if (primaryResult != null) {
            return formatLocationString(
                primaryResult.lineNum,
                primaryResult.column,
                primaryResult.functionName,
                primaryResult.fileName
            );
        }

        // Try fallback pattern
        const fallbackResult = parseSimplePattern(line);
        if (fallbackResult != null) {
            return formatLocationString(
                fallbackResult.lineNum,
                fallbackResult.column,
                fallbackResult.functionName,
                fallbackResult.fileName
            );
        }

        return null;
    }

    /**
     * Get the caller location from stack trace
     * Format: [filename.extension:line:column - function name]
     */
    function getCallerLocation(): string | null {
        try {
            const stack = new Error().stack;
            if (stack == null || stack === '') return null;

            const stackLines = stack.split('\n');
            const thisFilePattern = 'useConsoleLogger';

            // Skip the first 3 lines: Error, getCallerLocation, handleLogging
            // Then find the first line that's not from this file
            for (let i = 3; i < stackLines.length; i++) {
                const line = stackLines[i];
                if (line.includes(thisFilePattern)) continue;

                const location = parseLocationFromLine(line);
                if (location != null) return location;
            }

            return null;
        } catch {
            return null;
        }
    }

    /**
     * Central method to format log messages
     */
    function formatMessage(category: string, message: string | unknown[], includeLocation = true, hasCustomColor = false, customEmoji?: string): string | unknown[] {
        const emoji = customEmoji ?? getCategoryEmoji(category);
        const isVerbose = loggingMode.value === 'verbose';
        const location = (includeLocation && isVerbose) ? getCallerLocation() : null;
        const locationStr = location != null && location !== '' ? ` [${location}]` : '';
        // Make category bold using %c placeholder
        const header = `%c${category}%c ${emoji}${locationStr}`;
        const boldStyle = 'font-weight: bold';
        const normalStyle = '';

        // Add newline between header and message (only in verbose mode)
        // Add yellow/black color to message if no custom color is provided
        const colorStyle = hasCustomColor ? '' : `color: ${usesDarkMode ? '#f3cb4b' : '#282C31'}; background-color: ${usesDarkMode ? '#282C31' : '#E7E6E7'}`;
        const newline = isVerbose ? '\n' : ' ';

        if (Array.isArray(message)) {
            if (hasCustomColor) {
                return [header + newline, boldStyle, normalStyle, ...message];
            } else {
                // For arrays, add %c at the start and color style
                // The message array items will be displayed after the styled header
                return [header + newline + '%c', boldStyle, normalStyle, colorStyle, ...message];
            }
        }

        if (hasCustomColor) {
            return [`${header}${newline}${message}`, boldStyle, normalStyle];
        } else {
            // Use %c for color styling
            return [`${header}${newline}%c${message}`, boldStyle, normalStyle, colorStyle];
        }
    }

    /**
     * Check if an object is a LogMessage (has both cat and msg properties)
     */
    function isLogMessage(obj: unknown): obj is LogMessage {
        return (
            typeof obj === 'object' &&
            obj !== null &&
            'msg' in obj &&
            ('cat' in obj || true) // msg is required, cat is optional
        );
    }

    /**
     * Central method to handle logging with category filtering
     * Formats objects with {cat, msg} structure, passes everything else through
     */
    function handleLogging(args: unknown[], logMethod: (...args: unknown[]) => void): void {
        if (args.length === 0) return;

        const formattedArgs: unknown[] = [];
        let shouldLog = false;

        // Process each argument
        for (const arg of args) {
            if (isLogMessage(arg)) {
                // Check if we should log this category
                if (shouldLogCategory(arg.cat)) {
                    shouldLog = true;
                    // Check if custom color is provided
                    const hasCustomColor = 'color' in arg && arg.color != null;
                    // Check if custom emoji is provided
                    const customEmoji = arg.emoji;

                    // Format the message with emoji
                    let formattedMsg: string | unknown[];
                    if (arg.cat != null && arg.cat.trim() !== '') {
                        formattedMsg = formatMessage(arg.cat.toUpperCase(), arg.msg, true, hasCustomColor, customEmoji);
                    } else {
                        formattedMsg = arg.msg;
                    }

                    // If formattedMsg is an array, spread it; otherwise use it as single value
                    if (Array.isArray(formattedMsg)) {
                        formattedArgs.push(...formattedMsg);
                    } else {
                        formattedArgs.push(formattedMsg);
                    }
                }
                // If category is disabled, skip this LogMessage
            } else {
                // Get caller location and extract filename for auto-categorization
                const callerLocation = getCallerLocation();
                const callerFilename = callerLocation != null
                    ? callerLocation.split(':')[0] // Extract filename from "filename:line:column - function"
                    : undefined;
                const autoCategory = autoCategorizeMessage(arg, callerFilename);
                if (autoCategory !== undefined && shouldLogCategory(autoCategory)) {
                    shouldLog = true;
                    // Format the string with the auto-detected category
                    if (typeof arg === 'string') {
                        const formattedMsg = formatMessage(autoCategory.toUpperCase(), arg);
                        if (Array.isArray(formattedMsg)) {
                            formattedArgs.push(...formattedMsg);
                        } else {
                            formattedArgs.push(formattedMsg);
                        }
                    } else {
                        formattedArgs.push(arg);
                    }
                } else if (autoCategory === undefined) {
                    // No auto-category found, pass through as-is
                    formattedArgs.push(arg);
                    shouldLog = true;
                }
            }
        }

        // Only log if we have at least one enabled category or non-LogMessage args
        if (shouldLog || formattedArgs.length > 0) {
            logMethod(...formattedArgs);
        }
    }

    /**
     * Check if a category should be logged
     */
    function shouldLogCategory(category?: string): boolean {
        if (category == null || category.trim() === '') return true; // Always log messages without category
        if (!isDevMode.value) return false; // Only log in dev mode

        // Normalize category to lowercase for case-insensitive comparison
        const normalizedCategory = category.toLowerCase();

        // Check if category is explicitly disabled (case-insensitive)
        return loggingToggles.value[normalizedCategory] !== false;
    }

    /**
     * Enhanced console.log with category filtering
     */
    function enhancedLog(...args: unknown[]): void {
        handleLogging(args, originalConsole.log);
    }

    /**
     * Enhanced console.info with category filtering
     */
    function enhancedInfo(...args: unknown[]): void {
        handleLogging(args, originalConsole.info);
    }

    /**
     * Enhanced console.warn with category filtering
     */
    function enhancedWarn(...args: unknown[]): void {
        handleLogging(args, originalConsole.warn);
    }

    /**
     * Enhanced console.error with category filtering
     */
    function enhancedError(...args: unknown[]): void {
        handleLogging(args, originalConsole.error);
    }

    /**
     * Enhanced console.debug with category filtering
     */
    function enhancedDebug(...args: unknown[]): void {
        handleLogging(args, originalConsole.debug);
    }

    /**
     * Initialize the enhanced console logging system
     */
    function initConsoleLogger(): void {
        // Detect Safari browser
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        if (isSafari) {
            // Use ASCII art for Safari (background-image not supported)
            console.info('%c╔═╗╦ ╦╔═╗╔═╗╦═╗╔═╗ ', 'color: #a78230');
            console.info('%c╚═╗╚╦╝╚═╗╠═╣╠╦╝╠═╩╗', 'color: #a78230');
            console.info('%c╚═╝ ╩ ╚═╝╩ ╩╩╚═╚══╝', 'color: #a78230');
        } else {
            // Display sysarb logo in console using SVG (Chrome, Firefox, Edge)
            const christmasMonth = 11;
            const letters = new Date().getMonth() === christmasMonth
            ? ['#00AA00', '#ff0000', '#ffffff', '#ff0000', '#ffffff', '#ff0000']
            : ['#a78230', '#a78230', '#a78230', '#a78230', '#a78230', '#a78230'];
            const tree = new Date().getMonth() === christmasMonth
            ? '<g transform="translate(280, 50) scale(2.5)"><path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7H17Z" stroke="#00FF00"></path><path d="M12 22v-3" stroke="#00FF00"></path></g>'
            : '';

            const logo = 'data:image/svg+xml;base64,' + btoa(`
            <svg xmlns="http://www.w3.org/2000/svg" width="342" height="116" viewBox="0 0 342 116" fill="none">
            <g>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M8.64453 78.4012L8.85203 78.8862C9.87271 81.0281 11.3135 82.9423 13.0884 84.5147C15.2796 86.434 17.7983 87.9417 20.5238 88.9656C24.1334 90.2811 27.9581 90.9038 31.7978 90.8014C35.2327 90.8697 38.6523 90.3243 41.8961 89.1907C44.5349 88.2608 46.971 86.8321 49.072 84.9823C50.894 83.3477 52.3344 81.3314 53.2911 79.0767C54.1869 76.9557 54.6513 74.6772 54.6572 72.3744C54.6766 68.3475 53.1183 64.4735 50.317 61.5849C46.7259 58.1298 42.1404 55.894 37.2101 55.1943L28.7027 53.4625C27.2153 53.2501 25.8088 52.6532 24.6219 51.7306C24.0749 51.3013 23.6342 50.7513 23.3343 50.1235C23.0344 49.4957 22.8833 48.8069 22.8927 48.111C22.9028 47.2505 23.1261 46.406 23.5426 45.6533C23.9591 44.9006 24.5557 44.2633 25.2789 43.7987C26.9086 42.6397 28.8656 42.0328 30.8641 42.0668C32.3724 42.0251 33.8748 42.272 35.2907 42.7942C36.4327 43.2185 37.4789 43.866 38.3686 44.6992C39.0076 45.3012 39.5449 46.0031 39.9594 46.7775H53.7926C53.5349 45.1637 53.0335 43.5987 52.3055 42.1361C51.3572 40.1327 50.0368 38.3283 48.4149 36.8193C46.3915 35.0097 44.0448 33.5995 41.4984 32.6628C38.1093 31.4539 34.5303 30.8672 30.9333 30.931C27.9345 30.9006 24.9543 31.405 22.1319 32.4203C19.62 33.3354 17.2813 34.6706 15.2153 36.369C13.298 37.9592 11.7367 39.936 10.6331 42.1707C9.50867 44.3628 8.91626 46.7896 8.9039 49.254C8.87042 53.0872 10.2707 56.7941 12.8291 59.6452C15.4401 62.6413 19.7457 64.8408 25.4 65.9665L33.3022 67.6983C35.2106 67.9253 37.0124 68.7013 38.4896 69.9324C38.9814 70.4015 39.3729 70.9658 39.6404 71.5909C39.9079 72.2161 40.0459 72.8892 40.0459 73.5693C40.0452 74.418 39.8463 75.2547 39.4649 76.0125C39.0836 76.7703 38.5305 77.4282 37.8498 77.9336C36.0614 79.2238 33.8742 79.8374 31.6768 79.6655C30.1899 79.7744 28.6962 79.5854 27.283 79.1096C25.8698 78.6339 24.5654 77.8808 23.446 76.8945C22.5699 76.0459 21.85 75.0493 21.3192 73.9504L8.64453 78.4012Z" fill="${letters[0]}"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M99.9605 33.2861L85.6777 71.3524L71.5852 33.2861H57.1641L78.4153 90.6627L72.1558 107.34H85.7296L113.534 33.2861H99.9605Z" fill="${letters[1]}"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M115.852 78.4012L116.059 78.8862C117.08 81.0281 118.52 82.9423 120.295 84.5147C122.487 86.434 125.005 87.9418 127.731 88.9656C131.346 90.2824 135.177 90.9052 139.022 90.8014C142.451 90.8684 145.865 90.323 149.103 89.1907C151.742 88.2608 154.178 86.8321 156.279 84.9823C158.106 83.3523 159.548 81.3348 160.498 79.0767C161.4 76.957 161.87 74.6784 161.882 72.3744C161.894 68.3489 160.336 64.4776 157.541 61.5849C153.942 58.1324 149.351 55.8973 144.417 55.1943L135.91 53.4625C134.424 53.2448 133.019 52.6484 131.829 51.7306C131.282 51.3013 130.841 50.7513 130.541 50.1235C130.241 49.4957 130.09 48.8069 130.1 48.111C130.11 47.2505 130.333 46.406 130.75 45.6533C131.166 44.9006 131.763 44.2633 132.486 43.7987C134.116 42.6397 136.073 42.0328 138.071 42.0668C139.585 42.0287 141.092 42.2755 142.515 42.7942C143.653 43.2269 144.698 43.8735 145.593 44.6992C146.281 45.2842 146.865 45.9809 147.322 46.7602H161.155C160.883 45.1478 160.376 43.584 159.651 42.1188C158.66 40.1099 157.292 38.3107 155.622 36.8193C153.599 35.0097 151.252 33.5995 148.705 32.6628C145.316 31.4539 141.737 30.8672 138.14 30.931C135.142 30.9006 132.161 31.405 129.339 32.4204C126.827 33.3354 124.488 34.6706 122.422 36.369C120.505 37.9592 118.944 39.936 117.84 42.1707C116.713 44.3617 116.12 46.7892 116.111 49.254C116.069 53.0887 117.47 56.7986 120.036 59.6452C122.63 62.6413 126.953 64.8408 132.59 65.9665L140.492 67.6983C142.4 67.9253 144.202 68.7013 145.679 69.9324C146.171 70.4015 146.563 70.9658 146.83 71.5909C147.098 72.2161 147.236 72.8892 147.236 73.5694C147.245 74.4107 147.059 75.2427 146.693 76C146.327 76.7573 145.791 77.4192 145.126 77.9336C143.343 79.2213 141.162 79.8348 138.97 79.6655C137.481 79.7757 135.984 79.5875 134.568 79.1117C133.152 78.6359 131.844 77.8822 130.722 76.8945C129.851 76.0417 129.132 75.046 128.595 73.9504L115.852 78.4012Z" fill="${letters[2]}"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M196.154 30.3594C191.48 30.35 186.866 31.4279 182.678 33.5084C178.49 35.5888 174.842 38.6148 172.021 42.3481C169.199 46.0814 167.282 50.4199 166.42 55.0217C165.558 59.6235 165.775 64.363 167.053 68.8667C168.332 73.3703 170.637 77.5151 173.787 80.9745C176.937 84.4339 180.846 87.1133 185.207 88.8016C189.567 90.49 194.259 91.1411 198.914 90.7037C203.568 90.2663 208.057 88.7524 212.028 86.2812L212.529 85.9868V89.8488H226.363V31.26H212.529V35.1913L212.028 34.8796C207.261 31.9188 201.763 30.3531 196.154 30.3594ZM196.154 76.9984C192.91 77.0019 189.738 76.041 187.039 74.2375C184.34 72.434 182.236 69.869 180.994 66.8673C179.752 63.8656 179.427 60.5621 180.06 57.3753C180.693 54.1884 182.257 51.2614 184.552 48.965C186.847 46.6685 189.771 45.1059 192.954 44.4749C196.136 43.8439 199.434 44.173 202.43 45.4204C205.426 46.6679 207.985 48.7776 209.782 51.4824C211.58 54.1873 212.536 57.3657 212.529 60.615C212.525 64.965 210.799 69.1357 207.729 72.2132C204.66 75.2907 200.497 77.0239 196.154 77.0331" fill="${letters[3]}"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M236.148 89.849H249.982V60.2342C250.074 55.9979 251.797 51.9611 254.79 48.9665C257.783 45.9719 261.816 44.2508 266.045 44.1625V30.3076C260.542 30.3544 255.158 31.9183 250.483 34.8278L249.982 35.1395V31.2601H236.148V89.849Z" fill="${letters[4]}"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M286.779 85.8482L287.263 86.1599C291.836 88.9956 297.082 90.5564 302.459 90.6807C307.836 90.805 313.148 89.4885 317.847 86.8671C322.546 84.2458 326.46 80.4149 329.185 75.7706C331.91 71.1264 333.346 65.8373 333.346 60.4505C333.346 55.0636 331.91 49.7746 329.185 45.1303C326.46 40.486 322.546 36.6551 317.847 34.0338C313.148 31.4125 307.836 30.0959 302.459 30.2202C297.082 30.3446 291.836 31.9053 287.263 34.741L286.779 35.0527V8.65918L272.945 13.5257V89.8488H286.779V85.8482ZM303.154 76.8598C299.911 76.8598 296.742 75.8969 294.046 74.0929C291.351 72.2889 289.25 69.7247 288.009 66.7247C286.768 63.7247 286.444 60.4236 287.076 57.2388C287.709 54.054 289.27 51.1286 291.562 48.8325C293.855 46.5364 296.776 44.9727 299.956 44.3392C303.135 43.7057 306.431 44.0309 309.427 45.2735C312.422 46.5162 314.982 48.6205 316.783 51.3204C318.584 54.0204 319.546 57.1946 319.546 60.4418C319.541 64.7947 317.813 68.9681 314.74 72.046C311.666 75.124 307.5 76.8553 303.154 76.8598Z" fill="${letters[5]}"/>
            ${tree}
            </g>
            </svg>
            `);
            console.info('%c ', `font-size: 0; line-height: 50px; background-color: #${usesDarkMode ? '282C31' : 'E7E6E7'}; background-image: url(${logo}); background-size: contain; background-repeat: no-repeat; background-position: center center; padding: 30px 200px;`);
        }

        // Override console methods
        window.console.log = enhancedLog;
        window.console.info = enhancedInfo;
        window.console.warn = enhancedWarn;
        window.console.error = enhancedError;
        window.console.debug = enhancedDebug;

        // Also override global console reference
        console.log = enhancedLog;
        console.info = enhancedInfo;
        console.warn = enhancedWarn;
        console.error = enhancedError;
        console.debug = enhancedDebug;

        console.info({ cat: 'logger', msg: '✨ Enhanced console logger initialized. 🎛️ Run consoleControl() to open the control panel.' });
    }

    /**
     * Restore original console methods
     */
    function restoreConsole(): void {
        window.console.log = originalConsole.log;
        window.console.info = originalConsole.info;
        window.console.warn = originalConsole.warn;
        window.console.error = originalConsole.error;
        window.console.debug = originalConsole.debug;

        console.log = originalConsole.log;
        console.info = originalConsole.info;
        console.warn = originalConsole.warn;
        console.error = originalConsole.error;
        console.debug = originalConsole.debug;
    }

    /**
     * Toggle a category on/off (case-insensitive)
     */
    const toggleCategory = (category: string, enabled: boolean): void => {
        const normalizedCategory = category.toLowerCase();
        loggingToggles.value[normalizedCategory] = enabled;
    };

    /**
     * Enable a category (case-insensitive)
     */
    const enableCategory = (category: string): void => {
        toggleCategory(category, true);
    };

    /**
     * Disable a category (case-insensitive)
     */
    const disableCategory = (category: string): void => {
        toggleCategory(category, false);
    };

    /**
     * Get all available categories
     */
    const availableCategories = computed(() => {
        return Object.keys(loggingToggles.value);
    });

    /**
     * Check if a category is enabled (case-insensitive)
     */
    function isCategoryEnabled(category: string): boolean {
        const normalizedCategory = category.toLowerCase();
        return loggingToggles.value[normalizedCategory] !== false;
    }

    /**
     * Reset category toggles to default states
     */
    function resetCategoryToggles(): void {
        loggingToggles.value = { ...defaultToggleStatesObject };
    }

    /**
     * Set logging mode (simple or verbose)
     */
    function setLoggingMode(mode: 'simple' | 'verbose'): void {
        loggingMode.value = mode;
    }

    return {
        initConsoleLogger,
        restoreConsole,
        toggleCategory,
        enableCategory,
        disableCategory,
        availableCategories,
        isCategoryEnabled,
        resetCategoryToggles,
        loggingToggles: computed(() => loggingToggles.value),
        isDevMode: computed(() => isDevMode.value),
        // convert object to array, so it can easily be looped over in the control panel template
        toggleConfigurations: Object.entries(defaultToggleStates).map(([key, config]) => ({
            key,
            ...config
        })),
        loggingMode: computed(() => loggingMode.value),
        setLoggingMode,
    };
}
